import React, { useEffect, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Edit, FileText, MapPin, Package, Phone, Plus, Radio, Settings, Trash2 } from 'lucide-react';
import { Badge } from './ui/badge';
import ResourceUnits from './ResourceUnits';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ResourcesList = ({ onAddNew, onEdit, refreshTrigger = 0, currentExercise = null, positions = [] }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [trackerModal, setTrackerModal] = useState(null); // { code, callsign, link }

  useEffect(() => {
    fetchResources();
  }, [refreshTrigger, currentExercise?.id]);

  const fetchResources = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/resources`, { headers });
      // Strictly filter resources to only show those assigned to the current exercise
      if (currentExercise?.id || currentExercise?.exercise_name) {
        const exerciseResources = response.data.filter(resource => 
          resource.assigned_exercise === currentExercise?.id || 
          resource.assigned_exercise === currentExercise?.exercise_name
        );
        setResources(exerciseResources);
      } else {
        // No exercise context - show empty list
        setResources([]);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (resourceId) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/resources/${resourceId}`, { headers });
        setResources(prev => prev.filter(r => r.id !== resourceId));
      } catch (error) {
        console.error('Error deleting resource:', error);
      }
    }
  };

  const handleGenerateTrackerLink = async (resource) => {
    // If the resource already has a tracking code, show it directly
    if (resource.tracking_code) {
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/#tracker/${resource.tracking_code}`;
      setTrackerModal({
        code: resource.tracking_code,
        callsign: resource.callsign || resource.identification,
        link,
      });
      return;
    }

    // Generate a new tracking code
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/resources/${resource.id}/generate-tracking-code`, {}, { headers });
      const code = res.data.tracking_code;
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/#tracker/${code}`;
      
      // Update local state
      setResources(prev => prev.map(r => r.id === resource.id ? { ...r, tracking_code: code } : r));
      
      setTrackerModal({
        code,
        callsign: resource.callsign || resource.identification,
        link,
      });
    } catch (error) {
      console.error('Error generating tracking code:', error);
      alert('Failed to generate tracking code. Please try again.');
    }
  };

  const filteredResources = resources.filter(resource => {
    if (filter === 'equipment') return resource.resource_type.startsWith('Equipment');
    if (filter === 'supplies') return resource.resource_type.startsWith('Supplies');
    if (filter === 'insufficient') return resource.quantity_available < resource.quantity_needed;
    if (filter === 'involved') return resource.involved_in_exercise;
    return true; // 'all'
  });

  // Print function for Resources
  const printICS210 = () => {
    const currentDateTime = new Date().toLocaleString();
    const currentDate = new Date().toLocaleDateString();
    const exerciseName = currentExercise?.exercise_name || currentExercise?.name || 'N/A';
    
    // Build resource rows
    const buildResourceRows = () => {
      if (resources.length === 0) {
        return '<tr><td colspan="9" style="text-align: center;">No resources available.</td></tr>';
      }
      
      return resources.map((resource, index) => {
        const status = resource.quantity_available >= resource.quantity_needed ? 'sufficient' :
                      resource.quantity_available === 0 ? 'unavailable' : 'insufficient';
        const statusText = status === 'sufficient' ? 'Sufficient' :
                          status === 'unavailable' ? 'Unavailable' : 'Insufficient';
        const exerciseBadge = resource.involved_in_exercise ? '<br><span class="exercise-badge">Exercise</span>' : '';
        const contactPhone = resource.contact_phone ? '<br>' + resource.contact_phone : '';
        
        return '<tr>' +
          '<td>' + (index + 1) + '</td>' +
          '<td>' + (resource.identification || '-') + exerciseBadge + '</td>' +
          '<td>' + (resource.resource_type || '-') + '</td>' +
          '<td>' + (resource.description || '-') + '</td>' +
          '<td>' + (resource.location || '-') + '</td>' +
          '<td style="text-align: center;">' + (resource.request || 0) + '</td>' +
          '<td style="text-align: center;">' + (resource.quantity_available || 0) + '</td>' +
          '<td style="text-align: center;">' + (resource.quantity_needed || 0) + '</td>' +
          '<td class="status-' + status + '">' + statusText + '</td>' +
          '</tr>';
      }).join('');
    };
    
    const printContent = `
      <html>
        <head>
          <title>ICS 210 - Resource Status Change</title>
          <style>
            @page { size: landscape; margin: 0.5in; }
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.4; }
            .header { text-align: center; margin-bottom: 25px; border-bottom: 3px solid #2563eb; padding-bottom: 15px; }
            .header h1 { color: #2563eb; margin: 0 0 10px 0; font-size: 22px; }
            .header-info { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-top: 15px; text-align: left; }
            .header-info-item { border: 1px solid #ddd; padding: 8px 12px; background: #f9f9f9; }
            .header-info-label { font-weight: bold; color: #2563eb; font-size: 11px; }
            .header-info-value { font-size: 12px; margin-top: 2px; }
            .resource-table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 10px; }
            .resource-table th, .resource-table td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; vertical-align: top; }
            .resource-table th { background-color: #2563eb; color: white; font-weight: bold; font-size: 9px; }
            .resource-table tr:nth-child(even) { background-color: #f9f9f9; }
            .status-sufficient { color: #155724; font-weight: bold; }
            .status-insufficient { color: #856404; font-weight: bold; }
            .status-unavailable { color: #721c24; font-weight: bold; }
            .exercise-badge { background: #d1ecf1; color: #0c5460; padding: 2px 6px; border-radius: 3px; font-size: 9px; }
            .summary { margin: 20px 0; padding: 15px; background: #f0f8ff; border: 1px solid #2563eb; border-radius: 5px; }
            .summary h3 { margin: 0 0 10px 0; color: #2563eb; font-size: 14px; }
            .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
            .summary-item { text-align: center; }
            .summary-value { font-size: 20px; font-weight: bold; color: #2563eb; }
            .summary-label { font-size: 10px; color: #666; }
            .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print { 
              body { margin: 0; padding: 15px; }
              .resource-table { font-size: 9px; }
              .resource-table th, .resource-table td { padding: 4px 6px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 210 - Resource Status Change</h1>
            <div class="header-info">
              <div class="header-info-item">
                <div class="header-info-label">Exercise Name:</div>
                <div class="header-info-value">${exerciseName}</div>
              </div>
              <div class="header-info-item">
                <div class="header-info-label">Date Prepared:</div>
                <div class="header-info-value">${currentDate}</div>
              </div>
            </div>
          </div>

          <div class="summary">
            <h3>Resource Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-value">${resources.length}</div>
                <div class="summary-label">Total Resources</div>
              </div>
              <div class="summary-item">
                <div class="summary-value">${resources.filter(r => r.quantity_available >= r.quantity_needed).length}</div>
                <div class="summary-label">Sufficient</div>
              </div>
              <div class="summary-item">
                <div class="summary-value">${resources.filter(r => r.quantity_available < r.quantity_needed && r.quantity_available > 0).length}</div>
                <div class="summary-label">Insufficient</div>
              </div>
              <div class="summary-item">
                <div class="summary-value">${resources.filter(r => r.involved_in_exercise).length}</div>
                <div class="summary-label">In Exercise</div>
              </div>
            </div>
          </div>
          
          <table class="resource-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Resource ID</th>
                <th>Type</th>
                <th>Description</th>
                <th>Location</th>
                <th style="text-align: center;">Requested</th>
                <th style="text-align: center;">Available</th>
                <th style="text-align: center;">Needed</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${buildResourceRows()}
            </tbody>
          </table>
          
          <div class="footer">
            <div><strong>ICS 210 - Resource Status Change</strong></div>
            <div>Generated: ${currentDateTime} | Powered by EXRSIM - Emergency Response Simulation Platform</div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Print ICS 218 - Support Vehicle/Equipment Inventory
  const printICS218 = async () => {
    const token = secureStorage.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Use the currently-filtered set so the print matches what the user sees on-screen
    const source = (filteredResources && filteredResources.length > 0) ? filteredResources : resources;

    // Fetch units for each resource in parallel
    const resourcesWithUnits = await Promise.all(
      source.map(async (resource) => {
        try {
          const res = await axios.get(`${API}/resources/${resource.id}/units`, { headers });
          return { ...resource, units: res.data || [] };
        } catch {
          return { ...resource, units: [] };
        }
      })
    );

    const exerciseName = currentExercise?.exercise_name || 'N/A';
    const now = new Date().toLocaleString();
    const escapeHtml = (s) => (s === null || s === undefined ? '' : String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;'));
    const dash = (v) => (v === null || v === undefined || v === '') ? '&mdash;' : escapeHtml(v);
    const totalUnits = resourcesWithUnits.reduce((s, r) => s + (r.units || []).length, 0);

    const resourceBlocks = resourcesWithUnits.map((r, idx) => `
      <div class="resource-block">
        <div class="resource-title">${idx + 1}. ${escapeHtml(r.resource_type || 'Unnamed Resource')}${r.identification ? ` &mdash; ${escapeHtml(r.identification)}` : ''}</div>
        <table class="detail-table">
          <tr>
            <th>Order Request No.</th><td>${dash(r.order_request_no)}</td>
            <th>Incident ID #</th><td>${dash(r.incident_id)}</td>
            <th>Agency</th><td>${dash(r.agency)}</td>
          </tr>
          <tr>
            <th>Make / Model</th><td>${dash(r.vehicle_equipment_make_model)}</td>
            <th>Licence / ID</th><td>${dash(r.vehicle_licence_id)}</td>
            <th>Callsign</th><td>${dash(r.callsign)}</td>
          </tr>
          <tr>
            <th>Contact Person</th><td>${dash(r.contact_person)}</td>
            <th>Contact Phone</th><td>${dash(r.contact_phone)}</td>
            <th>Involved in Exercise</th><td>${r.involved_in_exercise ? 'Yes' : 'No'}</td>
          </tr>
          <tr>
            <th>Location</th><td>${dash(r.location)}</td>
            <th>Quantity Avail. / Needed</th><td>${dash(r.quantity_available)} / ${dash(r.quantity_needed)}</td>
            <th>Status</th><td>${dash(r.status)}</td>
          </tr>
          <tr>
            <th>Requested Arrival</th><td>${dash((r.requested_arr_datetime || '').replace('T', ' '))}</td>
            <th>Exercise Start</th><td>${dash(r.exercise_start_date)}</td>
            <th>Incident Release</th><td>${dash(r.incident_release_date)}</td>
          </tr>
          ${r.description ? `<tr><th>Description</th><td colspan="5">${escapeHtml(r.description)}</td></tr>` : ''}
          ${r.special_equip_supplies ? `<tr><th>Special Equipment</th><td colspan="5">${escapeHtml(r.special_equip_supplies)}</td></tr>` : ''}
          ${r.work_assignment_special_inst ? `<tr><th>Work Assignment</th><td colspan="5">${escapeHtml(r.work_assignment_special_inst)}</td></tr>` : ''}
        </table>

        ${(r.units || []).length > 0 ? `
          <div class="units-heading">Units (${r.units.length})</div>
          <table class="units-table">
            <thead>
              <tr>
                <th>Unit Name</th><th>Location</th><th>Start Date</th><th>Start Time</th>
                <th>Release Date</th><th>Release Time</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${r.units.map(u => `
                <tr>
                  <td>${dash(u.unit_name)}</td>
                  <td>${dash(u.location)}</td>
                  <td>${dash(u.exercise_start_date)}</td>
                  <td>${dash(u.exercise_start_time)}</td>
                  <td>${dash(u.incident_release_date)}</td>
                  <td>${dash(u.incident_release_time)}</td>
                  <td>${dash(u.status)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : '<div class="no-units">No individual units recorded for this resource.</div>'}
      </div>
    `).join('');

    const printContent = `<!DOCTYPE html>
<html><head><title>ICS 218 - Support Vehicle / Equipment Inventory</title>
<style>
  body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.4; }
  .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
  .header h1 { color: #f97316; margin: 0 0 5px 0; font-size: 22px; }
  .header p { margin: 3px 0; font-size: 13px; }
  .empty-state { text-align: center; padding: 40px 20px; background: #fff7ed; border: 1px dashed #f97316; border-radius: 6px; color: #9a3412; }
  .resource-block { margin: 18px 0 24px 0; page-break-inside: avoid; border: 1px solid #ddd; border-radius: 6px; overflow: hidden; }
  .resource-title { background: #f97316; color: #fff; padding: 8px 12px; font-size: 14px; font-weight: bold; }
  table.detail-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.detail-table th { background: #fff7ed; color: #9a3412; text-align: left; padding: 6px 8px; border: 1px solid #ddd; width: 12%; font-weight: 600; }
  table.detail-table td { padding: 6px 8px; border: 1px solid #ddd; width: 21%; }
  .units-heading { background: #fef3c7; color: #92400e; font-weight: bold; padding: 6px 12px; font-size: 12px; border-top: 1px solid #ddd; }
  table.units-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.units-table th { background: #f59e0b; color: #fff; text-align: left; padding: 6px 8px; border: 1px solid #ddd; }
  table.units-table td { padding: 6px 8px; border: 1px solid #ddd; }
  table.units-table tr:nth-child(even) td { background: #fefdf7; }
  .no-units { padding: 8px 12px; color: #6b7280; font-size: 11px; font-style: italic; border-top: 1px solid #ddd; }
  .footer { margin-top: 20px; text-align: center; font-size: 11px; color: #666; border-top: 2px solid #ddd; padding-top: 10px; }
  @media print { body { margin: 0; padding: 10px; } .resource-block { page-break-inside: avoid; } }
</style></head><body>
  <div class="header">
    <h1>ICS 218 - Support Vehicle / Equipment Inventory</h1>
    <p><strong>Exercise:</strong> ${escapeHtml(exerciseName)}</p>
    <p><strong>Generated:</strong> ${escapeHtml(now)}</p>
    <p><strong>Total Resources:</strong> ${resourcesWithUnits.length} &nbsp;|&nbsp; <strong>Total Units:</strong> ${totalUnits}</p>
  </div>
  ${resourcesWithUnits.length === 0
    ? '<div class="empty-state">No resources found for this exercise. Add a resource in Res. Management to see it here.</div>'
    : resourceBlocks}
  <div class="footer">
    <p>ICS 218 - Support Vehicle / Equipment Inventory | Powered by EXRSIM</p>
  </div>
</body></html>`;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  const getStatusBadge = (resource) => {
    if (resource.quantity_available >= resource.quantity_needed) {
      return <Badge variant="default" className="bg-green-600 text-white">Sufficient</Badge>;
    } else if (resource.quantity_available === 0) {
      return <Badge variant="destructive" className="bg-red-600 text-white">Unavailable</Badge>;
    } else {
      return <Badge variant="secondary" className="bg-yellow-600 text-black">Insufficient</Badge>;
    }
  };

  const getResourceTypeCategory = (type) => {
    if (type.startsWith('Equipment')) return { icon: Settings, color: 'text-green-400' };
    if (type.startsWith('Supplies')) return { icon: Package, color: 'text-orange-400' };
    return { icon: Package, color: 'text-gray-400' };
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-orange-500 mb-2">Resource Management</h1>
          <p className="text-gray-400">Manage vehicles, equipment and supplies for your emergency</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Label htmlFor="resource-filter" className="text-gray-300 text-sm">
              Filter:
            </Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger 
                id="resource-filter"
                className="w-44 bg-gray-700 border-gray-600 text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                <SelectItem value="all" className="text-white focus:bg-gray-600">
                  Show All ({resources.length})
                </SelectItem>
                <SelectItem value="equipment" className="text-white focus:bg-gray-600">
                  Equipment ({resources.filter(r => r.resource_type.startsWith('Equipment')).length})
                </SelectItem>
                <SelectItem value="supplies" className="text-white focus:bg-gray-600">
                  Supplies ({resources.filter(r => r.resource_type.startsWith('Supplies')).length})
                </SelectItem>
                <SelectItem value="insufficient" className="text-white focus:bg-gray-600">
                  Insufficient ({resources.filter(r => r.quantity_available < r.quantity_needed).length})
                </SelectItem>
                <SelectItem value="involved" className="text-white focus:bg-gray-600">
                  Involved ({resources.filter(r => r.involved_in_exercise).length})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            onClick={printICS210}
          >
            <FileText className="h-4 w-4 mr-2" />
            Print ICS 210
          </Button>
          <Button
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            onClick={printICS218}
          >
            <FileText className="h-4 w-4 mr-2" />
            Print ICS 218
          </Button>
          <Button 
            onClick={onAddNew}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Resource
          </Button>
        </div>
      </div>

      {/* Filter Status Indicator */}
      {!loading && resources.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <span>
              Showing {filteredResources.length} of {resources.length} resources
            </span>
            {filter === 'involved' && (
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                Exercise Resources Only
              </Badge>
            )}
            {filter === 'insufficient' && (
              <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                Insufficient Resources
              </Badge>
            )}
            {filter === 'equipment' && (
              <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                Equipment Only
              </Badge>
            )}
            {filter === 'supplies' && (
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                Supplies Only
              </Badge>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-12 text-center">
            <div className="text-gray-400">Loading resources...</div>
          </CardContent>
        </Card>
      ) : filteredResources.length === 0 && filter !== 'all' ? (
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-12 text-center">
            <Package className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Resources Found</h3>
            <p className="text-gray-400 mb-6">
              No resources match the current filter criteria.
            </p>
            <Button 
              variant="outline"
              onClick={() => setFilter('all')}
              className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
            >
              Show All Resources
            </Button>
          </CardContent>
        </Card>
      ) : filteredResources.length === 0 ? (
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-12 text-center">
            <Package className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Resources Yet</h3>
            <p className="text-gray-400 mb-6">
              Get started by adding your first resource for emergency exercises.
            </p>
            <Button 
              onClick={onAddNew}
              className="bg-orange-500 hover:bg-orange-600 text-black"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Resource
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {filteredResources.map((resource) => {
              const { icon: TypeIcon, color } = getResourceTypeCategory(resource.resource_type);
              
              return (
                <Card key={resource.id} className="bg-gray-900 border-gray-700 hover:border-orange-500/50 transition-colors w-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Resource Image - Left side */}
                      {resource.resource_image && (
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-800">
                            <img 
                              src={resource.resource_image} 
                              alt={resource.identification}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      )}

                      {/* Type Icon (if no image) */}
                      {!resource.resource_image && (
                        <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-800 flex items-center justify-center">
                          <TypeIcon className={`h-8 w-8 ${color}`} />
                        </div>
                      )}

                      {/* Resource Type */}
                      <div className="flex-shrink-0 w-48">
                        <h3 className="text-base font-semibold text-white truncate">
                          {resource.resource_type}
                        </h3>
                      </div>

                      {/* Description */}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-400 text-sm truncate">
                          {resource.description}
                        </p>
                      </div>

                      {/* Quantities */}
                      <div className="flex-shrink-0 text-center w-20">
                        <div className="text-xs text-gray-400">Available</div>
                        <div className="text-white font-semibold">{resource.quantity_available}</div>
                      </div>
                      <div className="flex-shrink-0 text-center w-20">
                        <div className="text-xs text-gray-400">Needed</div>
                        <div className="text-white font-semibold">{resource.quantity_needed}</div>
                      </div>

                      {/* Location */}
                      <div className="flex-shrink-0 w-32">
                        <div className="flex items-center text-sm">
                          <MapPin className="h-3 w-3 text-gray-400 mr-1 flex-shrink-0" />
                          <span className="text-gray-400 truncate">{resource.location}</span>
                        </div>
                        <div className="flex items-center text-sm mt-1">
                          <Phone className="h-3 w-3 text-gray-400 mr-1 flex-shrink-0" />
                          <span className="text-gray-400 truncate">{resource.contact_person}</span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex-shrink-0 flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(resource)}
                          className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(resource.id)}
                          className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    
                    {/* Individual Units */}
                    <ResourceUnits 
                      resourceId={resource.id} 
                      resourceName={resource.resource_type || resource.name}
                      exerciseId={currentExercise?.id}
                      positions={positions}
                    />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Tracker Link Modal */}
      {trackerModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setTrackerModal(null)}>
          <Card className="bg-gray-800 border-gray-600 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-white text-lg">Tracker Link</CardTitle>
              <p className="text-gray-400 text-sm">{trackerModal.callsign}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center bg-white p-4 rounded-lg" data-testid="tracker-qr-code">
                <QRCodeSVG
                  value={trackerModal.link}
                  size={200}
                  level="M"
                  includeMargin={false}
                />
              </div>

              {/* Tracking Code */}
              <div className="text-center">
                <div className="text-gray-400 text-xs mb-1">CODE</div>
                <div className="text-3xl font-mono font-bold text-orange-400 tracking-[0.3em]" data-testid="tracker-code-display">
                  {trackerModal.code}
                </div>
              </div>

              {/* Link */}
              <div className="bg-gray-900 rounded-lg p-3">
                <div className="text-gray-400 text-xs mb-1">LINK</div>
                <div className="text-sm text-blue-400 break-all font-mono" data-testid="tracker-link-display">
                  {trackerModal.link}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(trackerModal.link);
                    alert('Link copied to clipboard!');
                  }}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="tracker-copy-link"
                >
                  Copy Link
                </Button>
                <Button
                  onClick={() => setTrackerModal(null)}
                  variant="outline"
                  className="border-gray-600 text-gray-300"
                >
                  Close
                </Button>
              </div>

              <p className="text-gray-500 text-xs text-center">
                Share this QR code or link with the resource operator. No login required.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Exercise List View (separate from Dashboard)

export default ResourcesList;
